// noinspection SpellCheckingInspection

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';
import has from 'lodash.has';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

import { Col, Div, ExpanderColumn, Icon, Label, Row, Switch, Text } from '@pinpoint/design-kit';

import Rbac from 'src/containers/rbac';
import { mdlFilters, modelRBSF } from 'src/functions/filters';
import {
  all,
  any,
  checkAllTablesRowsExpanded,
  checkHiddenByRole,
  checkSufficientRole,
  noOp,
  none,
  valFromQs,
} from 'src/functions';
import { intersection } from 'src/functions/set';
import { Page } from 'src/containers';
import { appActions, modelsListActions } from 'src/store/actions';
import { ApproveModelDialog, DeleteModelDialog, HideModelDialog, CopyModelDialog } from 'src/components/dialogs';
import {
  ActuarialCellActions,
  CellActions,
  CellDate,
  CellHit,
  CellHitMatched,
  CellMiss,
  CellMissMatched,
  CellName,
  CellNumber,
  CellPercent,
  CellStatus,
  CellRegulatoryTag,
  CellTag,
  CellText,
  CellValidation,
  CellValidationMatched,
} from 'src/components/models/cells';
import { ROUTES } from 'src/config';
import CONSTANTS from 'src/store/constants';
import usePlatformConfig from 'src/hooks/usePlatformConfig';
import { Button, Message, StatusFilterButtonGroup } from 'src/components';
import { CellText as CellTextWithCopy } from 'src/components/cells';
import { getFileDownloadUrl } from 'src/store/api';
import { ApiResponseProp, ModelProp, ClientProp } from 'src/store/typings';
import { ViewModelFeatureToggle } from './feature_toggles';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

const noClaimsAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  if (has(model, 'modelData.metadataPayload.replaced_outcomes')) {
    const replacedVal = model.modelData.metadataPayload?.replaced_outcomes?.match_zero_claims;
    return Number.isFinite(replacedVal) ? replacedVal : '—';
  }
  const noClaims = Number(model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.match_zero_claims ?? 0);
  return Number.isFinite(noClaims) ? noClaims : null;
};
const someClaimsAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  if (has(model, 'modelData.metadataPayload.replaced_outcomes')) {
    const replacedVal = model.modelData.metadataPayload?.replaced_outcomes?.match_gte_one_claims;
    return Number.isFinite(replacedVal) ? replacedVal : '—';
  }
  const someClaims = Number(
    model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.match_gte_one_claims ?? 0
  );
  return Number.isFinite(someClaims) ? someClaims : null;
};

const expectedRiskAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  const riskPerc = Number(model?.modelData?.dataPayload?.risk_percentage);
  return Number.isFinite(riskPerc) ? riskPerc : '—';
};

const onboardedRiskAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  if (has(model, 'modelData.metadataPayload.replaced_outcomes')) {
    const replacedVal = [
      model.modelData.metadataPayload?.replaced_outcomes?.onb_gte_one_claims,
      model.modelData.metadataPayload?.replaced_outcomes?.onb_zero_claims,
    ];
    return replacedVal.every((val) => Number.isFinite(val))
      ? (100 * replacedVal[0]) / (replacedVal[0] + replacedVal[1])
      : '—';
  }
  const onbSomeClaims = Number(
    model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.onb_gte_one_claims ?? 0
  );
  const onbZeroClaims = Number(model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.onb_zero_claims ?? 0);
  return [onbSomeClaims, onbZeroClaims].every((val) => Number.isFinite(val))
    ? (100 * onbSomeClaims) / (onbSomeClaims + onbZeroClaims)
    : 0;
};

const matchedRiskAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  if (has(model, 'modelData.metadataPayload.replaced_outcomes')) {
    const replacedVal = [
      model.modelData.metadataPayload?.replaced_outcomes?.match_gte_one_claims,
      model.modelData.metadataPayload?.replaced_outcomes?.match_zero_claims,
    ];
    return replacedVal.every((val) => Number.isFinite(val))
      ? (100 * replacedVal[0]) / (replacedVal[0] + replacedVal[1])
      : '—';
  }
  const matchSomeClaims = Number(
    model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.match_gte_one_claims ?? 0
  );
  const matchZeroClaims = Number(
    model?.modelData?.dataPayload?.actuarialSegment?.dataset?.payload?.match_zero_claims ?? 0
  );
  return [matchSomeClaims, matchZeroClaims].every((v) => Number.isFinite(v))
    ? (100 * matchSomeClaims) / (matchSomeClaims + matchZeroClaims)
    : 0;
};

const matchedValAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  if (has(model, 'modelData.metadataPayload.replaced_outcomes')) {
    const replacedVal = model.modelData.metadataPayload?.replaced_outcomes?.validation_matched;
    return Number.isFinite(replacedVal) ? replacedVal : '—';
  }
  const clusters = model?.modelData?.dataPayload?.actuarialSegment?.clusters ?? [];
  const validationCluster = clusters.find((info) => info?.tag === 'ACTVAL');
  return validationCluster?.matched ?? 0;
};
const matchValPercAccessor = (model) => {
  const { dataModelType } = model;
  if (dataModelType !== 'ACTUARIAL') return null;
  if (has(model, 'modelData.metadataPayload.replaced_outcomes')) {
    const replacedVal = [
      model.modelData.metadataPayload?.replaced_outcomes?.segment_matched,
      model.modelData.metadataPayload?.replaced_outcomes?.validation_matched,
    ];
    return all(replacedVal) && Number.isFinite(replacedVal[1] / replacedVal[0])
      ? (replacedVal[1] / replacedVal[0]) * 100
      : '—';
  }
  const clusters = model?.modelData?.dataPayload?.actuarialSegment?.clusters ?? [];
  const segmentMatchedUsers = model?.modelData?.dataPayload?.actuarialSegment?.matched;
  const validationCluster = clusters.find((info) => info?.tag === 'ACTVAL');
  const validationMatchedUsers = validationCluster?.matched;
  if (segmentMatchedUsers && validationMatchedUsers) {
    const matchedPerc = validationMatchedUsers / segmentMatchedUsers;
    if (Number.isFinite(matchedPerc)) return matchedPerc * 100;
  }
  return 0;
};

const actActionsAccessor = (model) => {
  const { dataModelType, recentStatus, id, hiddenByRole, modelData, name: modelName } = model;
  const metadata = modelData?.metadataPayload ?? {};
  const validation = (modelData?.dataPayload?.actuarialSegment?.clusters ?? []).find((cluster) => {
    const { name } = cluster;
    return !!name?.includes?.('_validation');
  });
  return JSON.stringify({
    id,
    modelData,
    dataModelType,
    hiddenByRole,
    metadata,
    modelName,
    recentStatus,
    validation,
  });
};

const statusColW = '4rem';
const dtColW = '6rem';
const actionColW = '3rem';
const actTagColW = '10rem';
const classTagColW = '8rem';
const regulatoryColW = '1fr ';
const actTblCtrlW = '2rem 1rem';
const nameColW = 'minmax(12rem, 18rem)';
const refreshColW = '1.25fr ';
const modelIdColW = '1fr ';

function getModelRefreshVersion(model) {
  const refresh =
    get(model, 'modelData.metadataPayload.refresh') ||
    get(model, 'modelData.metadata_payload.refresh') ||
    get(model, 'modelData.dataPayload.actuarialSegment.refresh') ||
    get(model, 'modelData.dataPayload.positiveSegment.refresh') ||
    get(model, 'modelData.dataPayload.negativeSegment.refresh');
  const value = refresh?.displayName || refresh?.display_name || refresh?.name || refresh?.id;
  return value ? `${value}` : '-';
}

function makeColWidths(hasFrameworkAct, hasFrameworkClass, isInternalMetadataVisible) {
  const metadataColW = isInternalMetadataVisible ? `${refreshColW}${modelIdColW}` : '';
  return {
    actuarialOverheaderWidths: `${actTblCtrlW} ${nameColW} 2fr 3fr 2fr ${actTagColW} ${
      hasFrameworkAct ? regulatoryColW : ''
    }${metadataColW}${actionColW} ${dtColW} ${statusColW}`,
    actuarialColWidths: `${actTblCtrlW} ${nameColW} 1fr 1fr 1fr 1fr 1fr 1fr 1fr ${actTagColW} ${
      hasFrameworkAct ? regulatoryColW : ''
    }${metadataColW}${actionColW} ${dtColW} ${statusColW}`,
    classifierColWidths: `3rem ${nameColW} 1fr .5fr 1fr .5fr 1fr 0.5fr 0.5fr ${classTagColW} ${
      hasFrameworkClass ? regulatoryColW : ''
    }${metadataColW}${actionColW} ${dtColW} ${statusColW}`,
  };
}

function Models({
  history,
  app: {
    client,
    clients,
    profile: { email, role, siteRole, tenant, originalTenant },
    fetchClientsStatus,
  },
  modelsList: {
    models,
    fetchModelsStatus,
    selectedModelIds,
    gridState,
    controlledGridState,
    maxGridRowSelection,
    selectedGridRows,
    approveModelStatus,
    downloadUrl,
    fetchModelDownloadStatus,
    checkDeleteModelDependents,
  },
  fetchModels,
  selectGridRow,
  deselectGridRow,
  setGridState,
  editModel,
  showModel,
  hideModel,
  copyModel,
  checkDeleteModel,
  deleteModel,
  approveModel,
  enqueueNotification,
  deselectAllModelIds,
  fetchClientByTenant,
  fetchEntityDl,
  unset,
}) {
  const search = new URLSearchParams(history.location.search);
  const modelListFlat = useMemo(() => {
    const out = [];
    if (models)
      models.forEach((model) => {
        out.push({
          id: model.id,
          name: model.name,
          hiddenByRole: model.hiddenByRole,
          dataModelType: model?.dataModelType,
          metadata: model?.modelData?.metadataPayload,
        });
        model.modelGroup.forEach((subModel) =>
          out.push({
            id: subModel.id,
            name: subModel.name,
            hiddenByRole: subModel.hiddenByRole,
            dataModelType: subModel?.dataModelType,
            metadata: model?.modelData?.metadataPayload,
          })
        );
      });
    return out;
  }, [models]);
  const [shouldLoadModelStats, setShouldLoadModelStats] = useState(
    +(window.localStorage.getItem('shouldLoadModelStats') ?? 0)
  );
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [hideModalOpen, setHideModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(role === CONSTANTS.ROLE.ADMIN || siteRole === CONSTANTS.ROLE.SUPER_ADMIN);
  const [modelToApproveId, setModelToApproveId] = useState(null);
  const modelToApprove = useMemo(
    () => modelListFlat.find((model) => model.id === modelToApproveId),
    [modelListFlat, modelToApproveId]
  );
  const [modelToDeleteId, setModelToDeleteId] = useState(null);
  const [modelToDeleteType, setModelToDeleteType] = useState(null);
  const [modelToHideId, setModelToHideId] = useState(null);
  const [modelToCopyId, setModelToCopyId] = useState(null);
  const [disableModelResults, setDisableModelResults] = useState(false);
  const [disableModelFairnessResults, setDisableModelFairnessResults] = useState(false);
  const [disableInputDistributions, setDisableInputDistributions] = useState(false);
  const [rowsOpen, setRowsOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(valFromQs(search, 'filter', CONSTANTS.TAL_MODEL.FILTERS, ''));
  const [selectedClass, setSelectedClass] = useState(
    valFromQs(search, 'modelClass', CONSTANTS.TAL_MODEL.MODEL_CLASSES, '')
  );
  const isInternalMetadataVisible = useMemo(
    () => checkSufficientRole(siteRole, CONSTANTS.ROLE.CLIENT_SERVICES, true),
    [siteRole]
  );
  const getStatusGroup = useCallback((model) => modelRBSF(model, siteRole), [siteRole]);
  const disabledInputDistStr = useMemo(
    () =>
      disableInputDistributions ? "Select an Actuarial model's parent row" : 'View distributions of loss outcomes',
    [disableInputDistributions]
  );
  const disabledLiftChartsStr = useMemo(
    () => (disableModelResults ? 'Select a completed model' : 'View lift charts for selected model(s)'),
    [disableModelResults]
  );
  const disabledFairnessStr = useMemo(
    () => (disableModelResults ? 'Select a completed model' : 'View fairness for selected model'),
    [disableModelResults]
  );
  const disabledValidationAnalyticsStr = useMemo(
    () => (disableModelResults ? 'Select a completed model' : 'View validation set analytics'),
    [disableModelResults]
  );
  const disabledRoiCalculatorStr = useMemo(
    () => (disableModelResults ? 'Select a completed model' : 'View financial impact model'),
    [disableModelResults]
  );

  const selectedModelClasses = useMemo(() => {
    if (selectedModelIds.length > 0) {
      return selectedModelIds.map((modelId) =>
        modelListFlat.find((model) => model.id === modelId)?.dataModelType === 'CLASSIFIER' ? 'classifier' : 'actuarial'
      );
    }
    return [];
  }, [selectedModelIds, modelListFlat]);
  const handleDeleteModel = useCallback(() => deleteModel(modelToDeleteId), [modelToDeleteId, deleteModel]);
  const handleHideModel = useCallback(() => {
    const model = modelListFlat.find((elem) => elem.id === modelToHideId);
    const hidden = model.hiddenByRole;
    if (hidden) showModel(modelToHideId);
    else hideModel(modelToHideId);
  }, [modelToHideId, hideModel, modelListFlat, showModel]);
  const handleApproveModel = useCallback(
    (/** @type {string} */ visibleModelVariation) => {
      approveModel(modelToApproveId, modelToApprove.dataModelType, visibleModelVariation);
    },
    [approveModel, modelToApprove, modelToApproveId]
  );
  const validModels = useCallback(
    (idList) => {
      const filteredModels = models.filter(({ id }) => idList.includes(id));
      return filteredModels.every(
        (model) =>
          getStatusGroup(model) === CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED &&
          (model?.dataModelType.includes('ACT_') || model?.dataModelType === 'CLASSIFIER')
      );
    },
    [getStatusGroup, models]
  );
  const getData = useCallback(() => {
    const filter = {
      filters: [],
      order_by: [{ field: 'created_at', direction: 'desc' }],
      fields:
        'items{id,name,framework,hiddenByRole,typeDetail,predictionType,payload{version},trainingType,modelData{metadataPayload,dataPayload{actuarial_segment_id,positive_segment_id,negative_segment_id,validation_segment_id,pets_percentage,risk_percentage}},recentStatus,createdAt,dataModelType,modelGroup{id,name,framework,hiddenByRole,createdAt,payload{version},predictionType,dataModelType,typeDetail,recentStatus,trainingType,modelData{metadataPayload,dataPayload{actuarial_segment_id,positive_segment_id,negative_segment_id,validation_segment_id}}}},*',
    };
    mdlFilters(selectedCode, selectedClass, siteRole, filter);
    fetchModels(filter);
  }, [selectedCode, selectedClass, siteRole, fetchModels]);

  const removeInvalidJsonStateKeys = useCallback((row) => {
    const newRow = { ...row };
    // remove properties that contain circular references and methods
    ['allCells', 'cells', 'values'].forEach((prop) => delete newRow[prop]);
    return newRow;
  }, []);
  const handleActuarialSelectGridRow = useCallback(
    (row) => {
      selectGridRow(removeInvalidJsonStateKeys(row), 'actuarial');
    },
    [selectGridRow, removeInvalidJsonStateKeys]
  );
  const handleClassifierSelectGridRow = useCallback(
    (row) => {
      selectGridRow(removeInvalidJsonStateKeys(row), 'classifier');
    },
    [selectGridRow, removeInvalidJsonStateKeys]
  );
  const handleActuarialDeselectGridRow = useCallback(
    (row) => {
      deselectGridRow(row, 'actuarial');
    },
    [deselectGridRow]
  );
  const handleClassifierDeselectGridRow = useCallback(
    (row) => {
      deselectGridRow(row, 'classifier');
    },
    [deselectGridRow]
  );
  const handleActuarialSetGridState = useCallback(
    (state) => {
      setGridState(state, 'actuarial');
    },
    [setGridState]
  );
  const handleClassifierSetGridState = useCallback(
    (state) => {
      setGridState(state, 'classifier');
    },
    [setGridState]
  );

  const enableSelection = useCallback(
    (row) => {
      const isActuarial = CONSTANTS.TAL_MODEL.TAGS.slice(1).includes(row.original.dataModelType);
      if (isActuarial) {
        const actuarialSuccessMatched = getStatusGroup(row.original) === CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED;
        const actuarialMatchedName = [
          'CSAA, Auto Policy Cancellation, Client Baseline',
          'CSAA, Auto Policy Cancellation, Combined Model',
        ].includes(row.original.name);
        const isActuarialParent = row.depth === 0;

        return isActuarialParent || (actuarialSuccessMatched && !actuarialMatchedName);
      }
      return getStatusGroup(row.original) === CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED;
    },
    [getStatusGroup]
  );

  const numberSort = useMemo(
    () => (rowA, rowB, columnId) => {
      const a = Number(rowA.values[columnId]);
      const b = Number(rowB.values[columnId]);
      return a > b ? 1 : -1;
    },
    []
  );

  const platformConfig = usePlatformConfig();

  const classifierData = useMemo(
    () =>
      models
        .filter((model) => {
          let statusGroupTest;
          if ([CONSTANTS.TAL_MODEL.FILTER.ALL, '', null, undefined].includes(selectedCode)) {
            statusGroupTest = true;
          } else {
            statusGroupTest = getStatusGroup(model) === CONSTANTS.TAL_MODEL.STATE_GROUPS[selectedCode];
          }
          return (
            statusGroupTest &&
            model.dataModelType === CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER &&
            !checkHiddenByRole(siteRole || role, model.hiddenByRole) &&
            !get(model, 'modelData.metadataPayload.shared', false)
          );
        })
        .map((model) => ({ ...model, selectedCode })),
    [models, selectedCode, siteRole, role, getStatusGroup]
  );
  const hasClassfierData = !!classifierData?.length;
  const hasFrameworkClass = useMemo(() => classifierData.some((model) => model?.framework?.id), [classifierData]);
  const classifierCols = useMemo(() => {
    const cols = [
      {
        id: 'name',
        Header: (
          <Text textSize="small" p={{ l: '2rem' }} maxW="100%" data-tag="model-name">
            Model Name
          </Text>
        ),
        justify: 'left',
        cellJustify: 'left',
        Cell: ({ value, row }) =>
          CellName({
            value,
            row,
            getDeprecatedTag: platformConfig.getDeprecatedTag,
            'data-test': 'model-name',
            'data-tip': value,
            maxW: '10rem',
            align: 'center',
            m: { r: '0.5rem' },
            style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' },
          }),
        accessor: 'name',
        isEditable: isAdmin ? CONSTANTS.CONTROL_STATES.ENABLED : CONSTANTS.CONTROL_STATES.DISABLED,
        onEdit: (model, name) => editModel(model.id, { name, dataModelType: model.dataModelType }),
      },
      {
        Header: (
          <Div d="flex" align="center" p={{ l: '2rem' }}>
            <Icon name="Positive" size=".875rem" viewBox="0 0 50 50" color="tagHit" m={{ r: '.5em' }} />
            <Text textSize="small">HIT</Text>
          </Div>
        ),
        id: 'positiveSegment',
        disableSortBy: true,
        justify: 'left',
        cellJustify: 'left',
        style: { paddingLeft: '2rem' },
        accessor: 'modelData',
        Cell: ({ value }) => CellHit({ modelData: value }),
      },
      {
        Header: <Text textSize="small">Matched</Text>,
        id: 'positiveMatched',
        justify: 'center',
        accessor: ({ modelData }) => modelData?.dataPayload?.positiveSegment?.matched ?? null,
        sortType: numberSort,
        Cell: CellHitMatched,
      },
      {
        Header: (
          <Div d="flex" align="center" m={{ l: '2rem' }}>
            <Icon name="Negative" size=".875rem" viewBox="0 0 50 50" color="tagMiss" m={{ r: '.5em' }} />
            <Text textSize="small">MISS</Text>
          </Div>
        ),
        id: 'negativeEngagement',
        disableSortBy: true,
        justify: 'left',
        accessor: 'modelData',
        Cell: ({ value }) => CellMiss({ modelData: value }),
      },
      {
        Header: <Text textSize="small">Matched</Text>,
        id: 'negativeMatched',
        justify: 'center',
        accessor: ({ modelData }) => modelData?.dataPayload?.negativeSegment?.matched ?? null,
        sortType: numberSort,
        Cell: CellMissMatched,
      },
      {
        Header: (
          <Div d="flex" align="center" m={{ l: '2rem' }}>
            <Icon name="Security" size=".875rem" viewBox="0 0 10.29 12" color="tagValidation" m={{ r: '.5em' }} />
            <Text textSize="small">VALIDATION</Text>
          </Div>
        ),
        id: 'validationEngagement',
        disableSortBy: true,
        justify: 'left',
        accessor: 'modelData',
        Cell: ({ value }) => CellValidation({ modelData: value }),
      },
      {
        Header: (
          <Div justify="right">
            <Text textSize="small">Matched</Text>
          </Div>
        ),
        id: 'validationMatched',
        justify: 'center',
        accessor: ({ modelData }) => modelData?.dataPayload?.validationSegment?.matched ?? null,
        sortType: numberSort,
        Cell: CellValidationMatched,
      },
      {
        Header: (
          <Text textSize="small" data-test="risk-perc">
            Risk %
          </Text>
        ),
        id: 'petsPercentage',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: false,
        accessor: ({ modelData }) => modelData?.dataPayload?.pets_percentage ?? null,
        sortType: numberSort,
        Cell: ({ value }) => (value ? CellPercent({ value }) : '–'),
      },
      {
        id: 'tag',
        Header: (
          <Text textSize="small" p={{ l: '2rem' }}>
            Type
          </Text>
        ),
        justify: 'left',
        cellJustify: 'left',
        Cell: (value) => CellTag({ ...value, bg: 'white' }),
        accessor: ({ dataModelType, predictionType, typeDetail, modelData, payload, recentStatus }) => {
          const { platform } =
            (modelData?.dataPayload?.negativeSegment ?? modelData?.dataPayload?.positiveSegment)?.refresh ?? {};
          return {
            recentStatus,
            modelVersion: payload?.version ?? null,
            dataModelType,
            predictionType,
            typeDetail,
            isRemediated: modelData?.metadataPayload?.fairness ?? false,
            isLegacy: platformConfig.isLegacyPlatform(platform),
          };
        },
      },
      {
        Header: <Text textSize="small">Actions</Text>,
        id: 'actions',
        justify: 'left',
        disableSortBy: true,
        // eslint-disable-next-line react/prop-types
        accessor: ({ id, recentStatus, hiddenByRole, modelData }) => {
          const { platform } =
            (modelData?.dataPayload?.negativeSegment ?? modelData?.dataPayload?.positiveSegment)?.refresh ?? {};
          return CellActions({
            id,
            siteRole,
            recentStatus,
            hiddenByRole,
            setModelToHideId,
            setModelToCopyId,
            setModelToDeleteId,
            setModelToApproveId,
            setHideModalOpen,
            setCopyModalOpen,
            setDeleteModalOpen,
            checkDeleteModel,
            setApproveModalOpen,
            enableApproveModel: platformConfig.canAction(platform),
          });
        },
      },
      {
        Header: <Text textSize="small">Created</Text>,
        id: 'created',
        cellJustify: 'center',
        accessor: (entity) => dayjs.utc(get(entity, 'created_at', get(entity, 'createdAt', '1970-01-01'))).toDate(),
        sortType: 'datetime',
        Cell: CellDate,
      },
      {
        Header: <Text textSize="small">Status</Text>,
        id: 'status',
        cellJustify: 'center',
        justify: 'center',
        disableSortBy: false,
        accessor: (model) => ({
          value: getStatusGroup(model),
          stats: model?.stats,
          trainingType: model?.trainingType,
          isRemediated: model?.modelData?.metadataPayload?.fairness ?? false,
          fairnessMetadata: model?.modelData?.metadataPayload?.fairness ?? {},
        }),
        Cell: CellStatus,
      },
    ];
    if (hasFrameworkClass)
      cols.splice(-3, 0, {
        id: 'regulatory',
        Header: (
          <Text textSize="small" m={{ p: '1rem' }}>
            Regulatory
          </Text>
        ),
        disableSortBy: true,
        justify: 'left',
        cellJustify: 'left',
        Cell: (value) => CellRegulatoryTag({ ...value, bg: 'white' }),
        accessor: ({ framework }) => ({
          framework,
        }),
      });
    if (isInternalMetadataVisible)
      // @ts-ignore
      cols.splice(
        -3,
        0,
        {
          id: 'trainingRefresh',
          Header: <Text textSize="small">Refresh Version</Text>,
          disableSortBy: true,
          justify: 'left',
          cellJustify: 'left',
          accessor: getModelRefreshVersion,
          Cell: CellText,
        },
        {
          id: 'modelId',
          Header: <Text textSize="small">Model ID</Text>,
          disableSortBy: true,
          justify: 'left',
          cellJustify: 'left',
          accessor: (model) => get(model, 'id', '-'),
          Cell: ({ value }) => CellTextWithCopy({ value: `${value}`, copy: value !== '-' }),
        }
      );
    return cols;
  }, [
    isAdmin,
    numberSort,
    platformConfig,
    editModel,
    siteRole,
    checkDeleteModel,
    getStatusGroup,
    hasFrameworkClass,
    isInternalMetadataVisible,
  ]);

  const { hasFramework: hasFrameworkAct, outputData: actuarialData } = useMemo(() => {
    let hasFramework = false;
    const actuarialModels = models.filter((model) => {
      let statusGroupTest;
      if ([CONSTANTS.TAL_MODEL.FILTER.ALL, '', null, undefined].includes(selectedCode)) {
        statusGroupTest = true;
      } else {
        statusGroupTest = getStatusGroup(model) === CONSTANTS.TAL_MODEL.STATE_GROUPS[selectedCode];
      }
      return (
        statusGroupTest &&
        model.dataModelType === CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL &&
        !checkHiddenByRole(siteRole || role, model.hiddenByRole) &&
        !get(model, 'modelData.metadataPayload.shared', false)
      );
    });
    const outputData = actuarialModels.map((model) => {
      if (model?.framework?.id && hasFramework === false) hasFramework = true;
      const subRows = model.modelGroup
        .map((subModel) => ({
          ...subModel,
          selectedCode,
          modelData: {
            ...subModel.modelData,
            dataPayload: {
              ...subModel.modelData.dataPayload,
              actuarialSegment: model.modelData.dataPayload.actuarialSegment,
            },
          },
        }))
        .filter(
          (elem) =>
            !checkHiddenByRole(siteRole || role, elem.hiddenByRole) &&
            !elem.isDeleted &&
            !get(elem, 'modelData.metadataPayload.shared', false)
        );
      subRows.sort((a, b) => dayjs.utc(a.createdAt) < dayjs.utc(b.createdAt));
      subRows.forEach((subModel) => {
        if (subModel?.framework?.id && hasFramework === false) hasFramework = true;
      });
      return {
        ...model,
        selectedCode,
        subRows,
      };
    });
    return {
      hasFramework,
      outputData,
    };
  }, [models, selectedCode, siteRole, role, getStatusGroup]);
  const hasActuarialData = !!actuarialData?.length;
  const actuarialCols = useMemo(() => {
    const cols = [
      ExpanderColumn,
      {
        id: 'name',
        Header: (info) => {
          const expanded = checkAllTablesRowsExpanded(info);
          if (expanded !== rowsOpen) setRowsOpen(expanded);
          return CellText({ value: 'Model Name', textSize: 'small', textWeight: '700', p: { l: '2rem' } });
        },
        justify: 'left',
        cellJustify: 'left',
        Cell: ({ value, row }) =>
          CellName({
            value,
            row,
            getDeprecatedTag: platformConfig.getDeprecatedTag,
            'data-test': 'model-name',
            'data-tip': value,
            style: { overflow: 'visible', whiteSpace: 'nowrap' },
            maxW: platformConfig.showLegacyPlatformTags ? '10rem' : '14rem',
            minW: '10rem',
          }),
        accessor: 'name',
        isEditable: isAdmin ? CONSTANTS.CONTROL_STATES.ENABLED : CONSTANTS.CONTROL_STATES.DISABLED,
        onEdit: (model, name) => editModel(model.id, { name, dataModelType: model.dataModelType }),
      },
      {
        Header: (
          <Text textSize="small" data-test="0-claims">
            Non-Claimants
          </Text>
        ),
        id: 'no_claims',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: false,
        sortType: numberSort,
        accessor: noClaimsAccessor,
        Cell: CellNumber,
      },
      {
        Header: (
          <Text textSize="small" data-test="gte-1-claim">
            Claimants
          </Text>
        ),
        id: 'some_claims',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: false,
        sortType: numberSort,
        accessor: someClaimsAccessor,
        Cell: CellNumber,
      },
      {
        Header: <Text textSize="small">Expected</Text>,
        id: 'expected_risk',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: expectedRiskAccessor,
        Cell: CellPercent,
      },
      {
        Header: <Text textSize="small">Onboarded</Text>,
        id: 'onboarded_risk',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: onboardedRiskAccessor,
        Cell: CellPercent,
      },
      {
        Header: <Text textSize="small">Matched</Text>,
        id: 'matched_risk',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: matchedRiskAccessor,
        Cell: CellPercent,
      },
      {
        Header: <Text textSize="small">Matched</Text>,
        id: 'matched_validation',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: matchedValAccessor,
        Cell: CellNumber,
      },
      {
        Header: (
          <Text textSize="small" data-test="matched_perc">
            Percentage
          </Text>
        ),
        id: 'matched_validation_perc',
        justify: 'center',
        cellJustify: 'center',
        disableSortBy: true,
        accessor: matchValPercAccessor,
        Cell: CellPercent,
      },
      {
        id: 'tag',
        Header: (
          <Text textSize="small" p={{ l: '2rem' }}>
            Type
          </Text>
        ),
        disableSortBy: true,
        justify: 'left',
        cellJustify: 'left',
        Cell: (value) => CellTag({ ...value, bg: 'white' }),
        accessor: ({ dataModelType, typeDetail, predictionType, modelData, recentStatus, payload }) => {
          const { platform } = modelData?.dataPayload?.actuarialSegment?.refresh ?? {};
          return {
            recentStatus,
            modelVersion: payload?.version ?? null,
            dataModelType,
            typeDetail,
            predictionType,
            isRemediated: modelData?.metadataPayload?.fairness ?? false,
            isLegacy: platformConfig.isLegacyPlatform(platform),
          };
        },
      },
      {
        Header: <Text textSize="small">Actions</Text>,
        id: 'actions',
        justify: 'left',
        disableSortBy: true,
        // eslint-disable-next-line react/prop-types
        accessor: actActionsAccessor,
        Cell: ({ value, row }) => {
          const { platform } = row?.original?.modelData?.dataPayload?.actuarialSegment?.refresh ?? {};
          const childrenStatus = row?.original?.modelGroup?.map?.((mdl) => mdl?.recentStatus) ?? [];
          return value
            ? ActuarialCellActions({
                ...JSON.parse(value),
                role,
                siteRole,
                originalTenant,
                setModelToDeleteId,
                setModelToDeleteType,
                setModelToApproveId,
                setModelToCopyId,
                setModelToHideId,
                setCopyModalOpen,
                setDeleteModalOpen,
                checkDeleteModel,
                setApproveModalOpen,
                setHideModalOpen,
                fetchEntityDl,
                enableApproveModel: platformConfig.canAction(platform),
                childrenRecentStatusList: childrenStatus,
              })
            : null;
        },
      },
      {
        Header: <Text textSize="small">Created</Text>,
        id: 'created',
        cellJustify: 'center',
        accessor: (entity) => dayjs.utc(get(entity, 'created_at', get(entity, 'createdAt', '1970-01-01'))).toDate(),
        sortType: 'datetime',
        Cell: CellDate,
      },
      {
        Header: <Text textSize="small">Status</Text>,
        id: 'status',
        cellJustify: 'center',
        justify: 'center',
        disableSortBy: false,
        accessor: (model) => ({
          value: getStatusGroup(model),
          stats: model?.stats,
          trainingType: model?.trainingType,
          isRemediated: model?.modelData?.metadataPayload?.fairness ?? false,
          fairnessMetadata: model?.modelData?.metadataPayload?.fairness ?? {},
        }),
        Cell: CellStatus,
      },
    ];
    if (hasFrameworkAct)
      cols.splice(-3, 0, {
        id: 'regulatory',
        Header: (
          <Text textSize="small" m={{ p: '1rem' }}>
            Regulatory
          </Text>
        ),
        disableSortBy: true,
        justify: 'left',
        cellJustify: 'left',
        Cell: (value) => CellRegulatoryTag({ ...value, bg: 'white' }),
        accessor: ({ framework }) => ({
          framework,
        }),
      });
    if (isInternalMetadataVisible)
      // @ts-ignore
      cols.splice(
        -3,
        0,
        {
          id: 'trainingRefresh',
          Header: <Text textSize="small">Refresh Version</Text>,
          disableSortBy: true,
          justify: 'left',
          cellJustify: 'left',
          accessor: getModelRefreshVersion,
          Cell: CellText,
        },
        {
          id: 'modelId',
          Header: <Text textSize="small">Model ID</Text>,
          disableSortBy: true,
          justify: 'left',
          cellJustify: 'left',
          accessor: (model) => get(model, 'id', '-'),
          Cell: ({ value }) => CellTextWithCopy({ value: `${value}`, copy: value !== '-' }),
        }
      );
    return cols;
  }, [
    isAdmin,
    numberSort,
    rowsOpen,
    platformConfig,
    editModel,
    role,
    siteRole,
    originalTenant,
    checkDeleteModel,
    fetchEntityDl,
    getStatusGroup,
    hasFrameworkAct,
    isInternalMetadataVisible,
  ]);

  const { actuarialOverheaderWidths, actuarialColWidths, classifierColWidths } = useMemo(
    () => makeColWidths(hasFrameworkAct, hasFrameworkClass, isInternalMetadataVisible),
    [hasFrameworkClass, hasFrameworkAct, isInternalMetadataVisible]
  );

  const handleFilterButtonClick = useCallback(
    (filterType) => {
      deselectAllModelIds();
      if (selectedCode === filterType) return setSelectedCode('');
      return setSelectedCode(filterType);
    },
    [deselectAllModelIds, selectedCode, setSelectedCode]
  );

  useEffect(() => {
    if (approveModelStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('Approving the model failed. Please try again');
    }
    if (approveModelStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Model successfully approved', { autoClose: true });
    }
  }, [approveModelStatus, enqueueNotification]);

  useEffect(() => {
    fetchClientByTenant(tenant);
    const hasState = ![null, undefined].includes(selectedCode);
    const hasClass = ![null, undefined].includes(selectedClass);
    const query = `?filter=${selectedCode}&modelClass=${selectedClass}`;
    if (hasState || hasClass) {
      getData();
      history.replace({
        pathname: '/models',
        search: query,
        state: {},
      });
    }
  }, [tenant, selectedCode, selectedClass, fetchClientByTenant, history, getData, unset]);

  useEffect(() => {
    deselectAllModelIds();
  }, [tenant, deselectAllModelIds]);

  useEffect(() => {
    const badLength = selectedModelIds?.length !== 1;
    const blacklistedModels = intersection(
      new Set(['CSAA, Auto Policy Cancellation, Client Baseline', 'CSAA, Auto Policy Cancellation, Combined Model']),
      new Set([selectedGridRows?.classifier?.[0]?.original?.name, selectedGridRows?.actuarial?.[0]?.original?.name])
    ).size;
    const hasClassifiers = selectedGridRows?.classifier?.length;
    const classifierBadStatus =
      getStatusGroup(selectedGridRows?.classifier?.[0]?.original) !== CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED;
    const classifierFinishedLiftChart =
      selectedGridRows?.classifier?.[0]?.original?.modelData?.metadataPayload?.has_lift_chart;
    const classifierFinishedFairness =
      selectedGridRows?.classifier?.[0]?.original?.modelData?.metadataPayload?.has_fairness;
    const hasActuarials = selectedGridRows?.actuarial?.length;
    const isActuarialParent = hasActuarials && selectedGridRows?.actuarial?.[0]?.depth === 0;
    const actuarialBadStatus =
      getStatusGroup(selectedGridRows?.actuarial?.[0]?.original) !== CONSTANTS.TAL_MODEL.STATE_GROUPS.COMPLETED;
    const actuarialFinishedLiftChart =
      selectedGridRows?.actuarial?.[0]?.original?.modelData?.metadataPayload?.has_lift_chart;
    const actuarialFinishedFairness =
      selectedGridRows?.actuarial?.[0]?.original?.modelData?.metadataPayload?.has_fairness;
    if (badLength || blacklistedModels) {
      setDisableModelResults(true);
      setDisableModelFairnessResults(true);
      setDisableInputDistributions(true);
      return;
    }
    if (isActuarialParent) {
      setDisableModelResults(true);
      setDisableModelFairnessResults(true);
      setDisableInputDistributions(false);
      return;
    }
    if (hasClassifiers) {
      setDisableInputDistributions(true);
      setDisableModelResults(classifierBadStatus || !classifierFinishedLiftChart);
      setDisableModelFairnessResults(classifierBadStatus || !classifierFinishedFairness);
    } else {
      setDisableInputDistributions(true);
      setDisableModelResults(actuarialBadStatus || !actuarialFinishedLiftChart);
      setDisableModelFairnessResults(actuarialBadStatus || !actuarialFinishedFairness);
    }
  }, [selectedModelIds.length, selectedGridRows, getStatusGroup]);

  useEffect(() => {
    setIsAdmin(role === CONSTANTS.ROLE.ADMIN || siteRole === CONSTANTS.ROLE.SUPER_ADMIN);
  }, [role, siteRole]);

  useEffect(() => {
    if (fetchModelDownloadStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('Download failed. Please try again later');
    }
    if (fetchModelDownloadStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Download successfully completed', {
        autoClose: true,
      });
      window.open(downloadUrl, '_blank');
    }
  }, [downloadUrl, fetchModelDownloadStatus, enqueueNotification]);
  return (
    <Div
      flexDir="column"
      d="flex"
      h="100%"
      minH="100%"
      w="100%"
      minW="100%"
      data-debug="content"
      data-test="content"
      p={{ x: '-1.15rem' }}
    >
      <Row data-debug="nav-row" data-test="nav-row" m={{ x: '1.5rem', t: '1.5rem', b: '.9rem' }}>
        <Col gutterWidth="0" d="inline-flex" size="8">
          <Div data-test="inp-dst-div">
            <Button
              buttonStyle="primary"
              data-tip={hasActuarialData ? disabledInputDistStr : 'Actuarial model must be requested first'}
              disabled={disableInputDistributions}
              onClick={async () => {
                const selectedModel = modelListFlat.find(({ id }) => selectedModelIds[0] === id);
                const rplOut = selectedModel?.metadata?.replaced_outcomes;
                if (rplOut) {
                  const dlUrl = await getFileDownloadUrl(rplOut.bucket, rplOut.key);
                  window.open(dlUrl, '_blank');
                } else {
                  history.push(
                    ROUTES.MODEL_INPUT_DISTRIBUTIONS.replace(':modelId', selectedModelIds[0]).replace(
                      ':modelClass',
                      selectedModelClasses[0]
                    )
                  );
                }
              }}
            >
              <Icon
                name="DistributionChartEnclosed"
                color="white"
                m={{ r: '0.5rem' }}
                size="16px"
                viewBox="0 0 16 16"
              />
              INPUT DISTRIBUTIONS
            </Button>
          </Div>
          {selectedGridRows?.classifier?.length > 1 && (
            <Button
              buttonStyle="primary"
              disabled={
                selectedGridRows?.classifier.length < selectedModelIds.length ||
                selectedGridRows?.classifier.length <= 1 ||
                !validModels(get(selectedGridRows, 'classifier').map((model) => model.original.id))
              }
              onClick={() => {
                const query = get(selectedGridRows, 'classifier')
                  .map((model) => `id=${model.original.id}`)
                  .join('&');
                deselectAllModelIds();
                history.push(`${ROUTES.MODELS_COMPARE}?${query}`.replace(':modelClass', 'classifier'));
              }}
            >
              <Icon name="BarChartEnclosed" size="1rem" color="white" viewBox="0 0 16 17" m={{ r: '.2rem' }} />
              <Icon name="BarChartEnclosed" size="1rem" color="white" viewBox="0 0 16 17" m={{ r: '.2rem' }} />
              COMPARE LIFT CHARTS
            </Button>
          )}
          {selectedGridRows?.actuarial?.length > 1 && (
            <Button
              buttonStyle="primary"
              disabled={
                get(selectedGridRows, 'actuarial').length < selectedModelIds.length ||
                get(selectedGridRows, 'actuarial').length <= 1 ||
                !validModels(get(selectedGridRows, 'actuarial').map((model) => model.original.id))
              }
              onClick={() => {
                const query = get(selectedGridRows, 'actuarial')
                  .map((model) => `id=${model.original.id}`)
                  .join('&');
                deselectAllModelIds();
                history.push(`${ROUTES.MODELS_COMPARE}?${query}`.replace(':modelClass', 'actuarial'));
              }}
            >
              <Icon name="BarChartEnclosed" size="1rem" color="white" viewBox="0 0 16 17" m={{ r: '.2rem' }} />
              <Icon name="BarChartEnclosed" size="1rem" color="white" viewBox="0 0 16 17" m={{ r: '.2rem' }} />
              COMPARE LIFT CHARTS
            </Button>
          )}
          {any([
            all([[0, 1].includes(selectedGridRows?.classifier.length), selectedGridRows?.actuarial.length === 0]),
            all([[0, 1].includes(selectedGridRows?.actuarial.length), selectedGridRows?.classifier.length === 0]),
          ]) && (
            <Div data-test="lift-charts-btn">
              <Button
                buttonStyle="primary"
                data-tip={hasActuarialData || hasClassfierData ? disabledLiftChartsStr : 'Complete a model first'}
                disabled={disableModelResults}
                onClick={() => {
                  history.push(
                    ROUTES.MODEL_LIFT_CHARTS.replace(':modelId', selectedModelIds[0]).replace(
                      ':modelClass',
                      selectedModelClasses[0]
                    )
                  );
                }}
              >
                <Icon name="BarChartEnclosed" size="1rem" color="white" viewBox="0 0 16 17" m={{ r: '.5rem' }} />
                LIFT CHARTS
              </Button>
            </Div>
          )}
          {any([
            all([[0, 1].includes(selectedGridRows?.classifier.length), selectedGridRows?.actuarial.length === 0]),
            all([[0, 1].includes(selectedGridRows?.actuarial.length), selectedGridRows?.classifier.length === 0]),
          ]) && (
            <Div data-test="roi-calculator-btn">
              <Button
                buttonStyle="primary"
                data-tip={
                  any([hasActuarialData, hasClassfierData]) ? disabledRoiCalculatorStr : 'Complete a model first'
                }
                disabled={
                  disableModelResults ||
                  selectedGridRows?.classifier.length /* TODO: remove classifier check in the future once approved */
                }
                onClick={() => {
                  history.push(
                    ROUTES.MODEL_ROI_CALCULATOR.replace(':modelId', selectedModelIds[0]).replace(
                      ':modelClass',
                      selectedModelClasses[0]
                    )
                  );
                }}
              >
                <Icon name="Roi" viewBox="0 0 16 16" color="white" m={{ r: '0.5rem' }} size="1rem" />
                IMPACT CALCULATOR
              </Button>
            </Div>
          )}
          {selectedGridRows?.actuarial?.length > 1 && (
            <Button
              buttonStyle="primary"
              disabled={
                get(selectedGridRows, 'actuarial').length < selectedModelIds.length ||
                get(selectedGridRows, 'actuarial').length <= 1 ||
                !validModels(get(selectedGridRows, 'actuarial').map((model) => model.original.id))
              }
              onClick={() => {
                const query = get(selectedGridRows, 'actuarial')
                  .map((model) => `id=${model.original.id}`)
                  .join('&');
                deselectAllModelIds();
                const target = `${ROUTES.MODEL_ROI_CALCULATOR_CMP}?${query}`.replace(
                  ':modelClass',
                  selectedModelClasses[0]
                );
                history.push(target);
              }}
            >
              <Icon name="Roi" viewBox="0 0 16 16" color="white" m={{ r: '0.25rem' }} size="1rem" />
              <Icon name="Roi" viewBox="0 0 16 16" color="white" m={{ r: '0.5rem' }} size="1rem" />
              COMPARE IMPACT CALCULATORS
            </Button>
          )}
          {any([
            all([[0, 1].includes(selectedGridRows?.classifier.length), selectedGridRows?.actuarial.length === 0]),
            all([[0, 1].includes(selectedGridRows?.actuarial.length), selectedGridRows?.classifier.length === 0]),
          ]) && (
            <Div data-test="validation-analytics-btn">
              <Button
                buttonStyle="primary"
                data-tip={
                  any([hasActuarialData, hasClassfierData]) ? disabledValidationAnalyticsStr : 'Complete a model first'
                }
                disabled={disableModelResults}
                onClick={() => {
                  history.push(
                    ROUTES.MODEL_VALIDATION_ANALYTICS.replace(':modelId', selectedModelIds[0]).replace(
                      ':modelClass',
                      selectedModelClasses[0]
                    )
                  );
                }}
              >
                <Icon name="Customer" color="white" m={{ r: '0.5rem' }} size="1rem" />
                VALIDATION ANALYTICS
              </Button>
            </Div>
          )}
          <Rbac roles={CONSTANTS.ROLES.slice(0, 4)} fallback={null}>
            <Button
              buttonStyle="admin"
              data-tip={
                none([hasActuarialData, hasClassfierData])
                  ? 'Client must request a model first'
                  : 'View requirements prior to model approval'
              }
              data-test="aprv-cl-btn"
              disabled={!hasActuarialData && !hasClassfierData}
            >
              <Icon name="Checklist" color="white" m={{ r: '0.5rem' }} viewBox="0 0 24 24" size="1.25rem" />
              APPROVAL CHECKLIST
            </Button>
          </Rbac>
          {any([
            all([[0, 1].includes(selectedGridRows?.classifier.length), selectedGridRows?.actuarial.length === 0]),
            all([[0, 1].includes(selectedGridRows?.actuarial.length), selectedGridRows?.classifier.length === 0]),
          ]) && (
            <Rbac roles={CONSTANTS.ROLES.slice(0, 3)} fallback={null}>
              <Div data-test="fairness-btn">
                <Button
                  buttonStyle="admin"
                  data-tip={hasActuarialData || hasClassfierData ? disabledFairnessStr : 'Complete a model first'}
                  disabled={disableModelFairnessResults}
                  onClick={() => {
                    history.push(
                      ROUTES.MODEL_FAIRNESS.replace(':modelId', selectedModelIds[0]).replace(
                        ':modelClass',
                        selectedModelClasses[0]
                      )
                    );
                  }}
                >
                  <Icon name="Fairness" size="1rem" viewBox="0 0 37 36" color="white" m={{ r: '.5rem' }} />
                  FAIRNESS
                </Button>
              </Div>
            </Rbac>
          )}
        </Col>
        <Col flexGrow="1" d="flex" justify="right" align="center" gutterWidth="0" data-test="filter-btns">
          <Icon name="Filter" viewBox="0 0 17 16" size="1rem" m={{ x: '.625rem' }} data-tip="View filter" />
          <Button
            buttonStyle="tab"
            data-tip="View Actuarial models only"
            selected={selectedClass === CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL}
            onClick={() => {
              if (selectedClass !== CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL) {
                unset('models');
                deselectAllModelIds();
                setSelectedClass(CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL);
              }
            }}
          >
            <Icon name="ActuarialModel" size="1rem" viewBox="0 0 12 12" color="white" m={{ r: '.5em' }} />
            <Text textSize="body">ACTUARIAL</Text>
          </Button>
          <Button
            buttonStyle="tab"
            data-tip="View Classifier models only"
            selected={selectedClass === CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER}
            onClick={() => {
              if (selectedClass !== CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER) {
                unset('models');
                deselectAllModelIds();
                setSelectedClass(CONSTANTS.TAL_MODEL.MODEL_CLASS.CLASSIFIER);
              }
            }}
          >
            <Icon name="ClassifierModel" size="1rem" viewBox="0 0 10.46 16" color="white" m={{ r: '.5em' }} />
            <Text textSize="body">CLASSIFIER</Text>
          </Button>
          <Button
            buttonStyle="tab"
            m={{ r: '0rem' }}
            data-tip="View all model types"
            selected={['', CONSTANTS.TAL_MODEL.MODEL_CLASS.ALL].includes(selectedClass)}
            onClick={() => {
              if (!['', CONSTANTS.TAL_MODEL.MODEL_CLASS.ALL].includes(selectedClass)) {
                unset('models');
                deselectAllModelIds();
                setSelectedClass(CONSTANTS.TAL_MODEL.MODEL_CLASS.ALL);
              }
            }}
          >
            <Text textSize="body">ALL</Text>
          </Button>
        </Col>
      </Row>

      {fetchModelsStatus === CONSTANTS.API.STARTED && !hasActuarialData && !hasClassfierData && <Message />}

      {!hasActuarialData && !hasClassfierData && (
        <Row data-debug="actuarial-row" data-test="actuarial-row" m={{ x: '1.5rem', b: '.5rem' }}>
          <Col size="12" rounded="md" bg="white" gutterWidth="0">
            <Row
              data-debug="filter-row"
              data-test="filter-row"
              align="center"
              m={{ t: '1rem', b: '.75rem' }}
              flexWrap="nowrap"
            >
              <Col size="12" textAlign="right" data-test="filter-status-btns">
                <StatusFilterButtonGroup
                  selectedCode={selectedCode}
                  apiStatus={fetchModelsStatus}
                  onFilterButtonClick={handleFilterButtonClick}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      {hasActuarialData && (
        <Row data-debug="actuarial-row" data-test="actuarial-row" m={{ x: '1.5rem', b: '.5rem' }}>
          <Col size="12" rounded="md" bg="white" gutterWidth="0">
            {hasActuarialData && (
              <Row
                data-debug="filter-row"
                data-test="filter-row"
                align="center"
                m={{ t: '1rem', b: '.75rem' }}
                flexWrap="nowrap"
              >
                <Col size="4" gutterWidth="1.5rem">
                  <Text textSize="subtitle" textWeight="500" textColor="navyBlue" m={{ l: '2rem' }}>
                    Actuarial Loss Models
                  </Text>
                </Col>
                <Col size="8" textAlign="right" data-test="filter-status-btns">
                  <StatusFilterButtonGroup
                    selectedCode={selectedCode}
                    apiStatus={fetchModelsStatus}
                    onFilterButtonClick={handleFilterButtonClick}
                  />
                </Col>
              </Row>
            )}
            <Row
              data-debug="actuarial-table-row"
              data-test="actuarial-table-row"
              align="center"
              m={{ t: '1rem', b: '.75rem' }}
            >
              <Col m={{ y: '0rem' }} size="12">
                <Col
                  bg="shadeF2"
                  gutterWidth="0"
                  h="calc(1.75rem + 3px)"
                  style={{
                    marginBottom: '-2px',
                  }}
                  rounded={{ tr: 'sm', tl: 'sm' }}
                >
                  <Col
                    d="grid"
                    gutterWidth="0"
                    style={{
                      gridTemplateColumns: actuarialOverheaderWidths,
                    }}
                  >
                    <Div className="column" />
                    <Div className="column" />
                    <Div className="column" />
                    <Div className="column" />
                    <Div
                      border={{ b: '1px dashed' }}
                      borderColor="shadeAF"
                      className="column"
                      textAlign="center"
                      m={{ x: '.5rem' }}
                      h="1.75rem"
                      // bg="blueLight"
                    >
                      <Text
                        data-test="dataset-claim-freq"
                        textWeight="700"
                        textSize="small"
                        p={{ t: '.625rem', b: '.5rem' }}
                      >
                        DATASET CLAIMANT FREQUENCY
                      </Text>
                    </Div>
                    <Div
                      border={{ b: '1px dashed' }}
                      borderColor="shadeAF"
                      className="column"
                      textAlign="center"
                      h="1.75rem"
                      m={{ x: '.5rem' }}
                      // bg="greenLight"
                    >
                      <Text textWeight="700" textSize="small" p={{ t: '.625rem', b: '.5rem' }}>
                        VALIDATION RECORDS
                      </Text>
                    </Div>
                    <Div className="column" />
                    <Div className="column" />
                    <Div className="column" />
                    <Div className="column" />
                    {isInternalMetadataVisible && (
                      <>
                        <Div className="column" />
                        <Div className="column" />
                      </>
                    )}
                    <Div className="column" />
                  </Col>
                </Col>
              </Col>
              <Col size="12">
                <ViewModelFeatureToggle
                  tenant={tenant}
                  hasData={hasActuarialData}
                  getData={getData}
                  columns={actuarialCols}
                  data={actuarialData}
                  gridState={gridState.actuarial}
                  controlledGridState={controlledGridState.actuarial}
                  rawData={models}
                  onRowSelect={handleActuarialSelectGridRow}
                  onRowDeselect={handleActuarialDeselectGridRow}
                  onRowExpand={setRowsOpen}
                  enableSelection={enableSelection}
                  selectionTooltip={() => 'Select'}
                  hasSelection
                  hasExpansion
                  maxSelection={maxGridRowSelection.actuarial}
                  apiStatus={fetchModelsStatus}
                  setGridState={handleActuarialSetGridState}
                  subRowsColsIndentIndexes={[1, 2, 3]}
                  widths={actuarialColWidths}
                  DivProps={{
                    m: '0rem',
                    p: '0rem',
                    'data-test': 'actuarial-grid-wrapper',
                  }}
                  TableProps={{
                    p: { y: '0rem' },
                    'data-test': 'actuarial-table',
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      {hasClassfierData && (
        <Row
          data-debug="classifier-row"
          data-test="classifier-row"
          m={{ x: '1.5rem', t: hasActuarialData ? '.5rem' : '0rem' }}
        >
          <Col size="12" rounded="md" bg="white" gutterWidth="0">
            {!hasActuarialData && (
              <Row align="center" m={{ t: '1rem', b: '.75rem' }} flexWrap="nowrap" data-test="classifier-title-row">
                <Col data-test="classifier-title" size="4" gutterWidth="1.5rem">
                  <Text textSize="subtitle" textWeight="500" textColor="navyBlue" m={{ l: '2rem' }}>
                    Classifier Models
                  </Text>
                </Col>
                <Col size="8" textAlign="right" data-test="filter-status-btns">
                  <StatusFilterButtonGroup
                    selectedCode={selectedCode}
                    apiStatus={fetchModelsStatus}
                    onFilterButtonClick={handleFilterButtonClick}
                  />
                </Col>
              </Row>
            )}
            {hasActuarialData && (
              <Row align="center" m={{ t: '1rem', b: '.75rem' }} flexWrap="nowrap" data-test="classifier-title-row">
                <Col size="12" gutterWidth="1.5rem">
                  <Text textSize="subtitle" textWeight="500" textColor="navyBlue" m={{ l: '2rem' }}>
                    Classifier Models
                  </Text>
                </Col>
              </Row>
            )}
            <Row
              data-debug="classifier-table-row"
              data-test="classifier-table-row"
              flexGrow="1"
              align="center"
              m={{ t: '1rem', b: '.75rem' }}
            >
              <Col size="12">
                <ViewModelFeatureToggle
                  tenant={tenant}
                  hasData={hasClassfierData}
                  getData={getData}
                  columns={classifierCols}
                  data={classifierData}
                  gridState={gridState.classifier}
                  controlledGridState={controlledGridState.classifier}
                  rawData={models}
                  onRowSelect={handleClassifierSelectGridRow}
                  onRowDeselect={handleClassifierDeselectGridRow}
                  enableSelection={enableSelection}
                  hasSelection
                  selectionTooltip={() => 'Select'}
                  hasExpansion={false}
                  maxSelection={maxGridRowSelection.classifier}
                  apiStatus={fetchModelsStatus}
                  setGridState={handleClassifierSetGridState}
                  widths={classifierColWidths}
                  DivProps={{
                    m: '0rem',
                    p: '0rem',
                    'data-test': 'classifier-grid-wrapper',
                  }}
                  TableProps={{
                    p: { y: '0rem' },
                    'data-test': 'classifier-table',
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      <Rbac roles={CONSTANTS.ROLES.slice(0, 2)} fallback={null}>
        <Row data-debug="admin-button-row" data-test="admin-button-row" m={{ x: '1.5rem', t: '.5rem' }}>
          <Col size="12">
            <Label
              tag="div"
              align="center"
              justify="flex-end"
              data-test="model-stats-toggle"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const nextValue = +!+(window.localStorage.getItem('shouldLoadModelStats') ?? 0);
                window.localStorage.setItem('shouldLoadModelStats', `${nextValue}`);
                setShouldLoadModelStats(nextValue);
                if (nextValue) getData();
              }}
            >
              <Text textSize="subtitle" h="1.125rem" style={{ whiteSpace: 'nowrap' }} m={{ r: '.5rem' }}>
                Stats
              </Text>
              <Switch checked={shouldLoadModelStats} activeColor="brand500" activeShadow="0" />
            </Label>
          </Col>
        </Row>
      </Rbac>

      <HideModelDialog
        isOpen={hideModalOpen}
        model={modelListFlat.find((model) => model.id === modelToHideId)}
        actions={{
          cancel: {
            on: noOp,
          },
          close: {
            on: () => {
              setHideModalOpen(false);
              setModelToHideId(null);
            },
          },
          handlers: [{ on: handleHideModel, after: getData }],
        }}
      />
      <CopyModelDialog
        isOpen={copyModalOpen}
        modelId={modelToCopyId}
        modelName={modelListFlat.find((model) => model.id === modelToCopyId)?.name}
        clients={clients}
        fetchClientsStatus={fetchClientsStatus}
        tenant={tenant}
        actions={{
          cancel: {
            on: noOp,
          },
          close: {
            on: () => {
              setCopyModalOpen(false);
              setModelToCopyId(null);
            },
          },
          handlers: [{ on: copyModel, after: getData }],
        }}
      />
      <DeleteModelDialog
        isOpen={deleteModalOpen}
        dependentResources={checkDeleteModelDependents}
        dataModelType={modelToDeleteType}
        actions={{
          cancel: {
            on: noOp,
          },
          close: {
            on: () => {
              setDeleteModalOpen(false);
              setModelToDeleteId(null);
              setModelToDeleteType(null);
              window.setTimeout(() => unset('checkDeleteModelDependents'), 500);
            },
          },
          handlers: [{ on: handleDeleteModel, after: getData }],
        }}
      />
      <ApproveModelDialog
        isOpen={approveModalOpen}
        isHidden={Boolean(modelToApprove?.hiddenByRole)}
        actions={{
          cancel: {
            on: noOp,
          },
          close: {
            on: () => {
              setApproveModalOpen(false);
              setModelToApproveId(null);
            },
          },
          handlers: [{ on: handleApproveModel, after: getData }],
        }}
      />
    </Div>
  );
}

const mapStateToProps = ({ app, modelsList }) => ({ app, modelsList });

const mapDispatchToProps = (dispatch) => ({
  fetchModels: (...params) => dispatch(modelsListActions.fetchModels(...params)),
  selectGridRow: (row, type) => dispatch(modelsListActions.selectGridRow(row, type)),
  deselectGridRow: (row, type) => dispatch(modelsListActions.deselectGridRow(row, type)),
  deselectAllModelIds: (...params) => dispatch(modelsListActions.deselectAllModelIds(...params)),
  setGridState: (state, type) => dispatch(modelsListActions.setGridState(state, type)),
  editModel: (...params) => dispatch(modelsListActions.editModel(...params)),
  hideModel: (...params) => dispatch(modelsListActions.hideModel(...params)),
  copyModel: (params) => dispatch(modelsListActions.copyModelToClient(params)),
  showModel: (...params) => dispatch(modelsListActions.showModel(...params)),
  deleteModel: (...params) => dispatch(modelsListActions.deleteModel(...params)),
  checkDeleteModel: (...params) => dispatch(modelsListActions.checkDeleteModel(...params)),
  unset: (key) => dispatch(modelsListActions.unset(key)),
  approveModel: (...params) => dispatch(modelsListActions.approveModel(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
  fetchClientByTenant: (...params) => dispatch(modelsListActions.fetchClientByTenant(...params)),
  fetchEntityDl: (...params) => dispatch(modelsListActions.fetchModelDownloadFromEntity(...params)),
});

Models.propTypes = {
  app: PropTypes.shape({
    profile: PropTypes.shape({
      originalTenant: PropTypes.string.isRequired,
      tenant: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      siteRole: PropTypes.oneOf([PropTypes.string, null]).isRequired,
      email: PropTypes.string.isRequired,
    }),
    fetchClientsStatus: ApiResponseProp,
    client: ClientProp,
    clients: PropTypes.arrayOf(ClientProp).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  }).isRequired,
  modelsList: PropTypes.shape({
    checkDeleteModelDependents: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      })
    ).isRequired,
    models: PropTypes.arrayOf(ModelProp),
    fetchModelsStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    fetchModelDownloadStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    downloadUrl: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    selectedModelIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    gridState: PropTypes.shape({
      classifier: PropTypes.shape({}).isRequired,
      actuarial: PropTypes.shape({}).isRequired,
    }).isRequired,
    controlledGridState: PropTypes.shape({
      classifier: PropTypes.shape({}).isRequired,
      actuarial: PropTypes.shape({}).isRequired,
    }).isRequired,
    maxGridRowSelection: PropTypes.shape({
      actuarial: PropTypes.number.isRequired,
      classifier: PropTypes.number.isRequired,
    }).isRequired,
    selectedGridRows: PropTypes.shape({
      classifier: PropTypes.arrayOf(
        PropTypes.shape({
          original: ModelProp,
          depth: PropTypes.number.isRequired,
        })
      ).isRequired,
      actuarial: PropTypes.arrayOf(
        PropTypes.shape({
          original: ModelProp,
          depth: PropTypes.number.isRequired,
        })
      ).isRequired,
    }).isRequired,
    approveModelStatus: PropTypes.string.isRequired,
  }).isRequired,
  fetchModels: PropTypes.func.isRequired,
  selectGridRow: PropTypes.func.isRequired,
  deselectGridRow: PropTypes.func.isRequired,
  deselectAllModelIds: PropTypes.func.isRequired,
  setGridState: PropTypes.func.isRequired,
  editModel: PropTypes.func.isRequired,
  showModel: PropTypes.func.isRequired,
  hideModel: PropTypes.func.isRequired,
  copyModel: PropTypes.func.isRequired,
  deleteModel: PropTypes.func.isRequired,
  checkDeleteModel: PropTypes.func.isRequired,
  approveModel: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  fetchClientByTenant: PropTypes.func.isRequired,
  fetchEntityDl: PropTypes.func.isRequired,
  unset: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(Models));
